import React, { useState, useEffect } from 'react';
import { useAuth } from '../context/AuthContext';
import { useNavigate, useSearchParams } from 'react-router-dom';
import axios from 'axios';
import { EditorHero, MonoLabel, Monogram, Pill } from '../components/Editorial';
import { isAthlete as isAthleteRole } from '../utils/roles';
import AdminPage, { usesRail } from '../components/AdminShell';
import '../components/lists/Lists.css';
import './SettingsPage.css';
import Button from '../components/Button';
import BillingAthletePicker from '../components/BillingAthletePicker';
import { useBillingTarget, billingBody, billingTargetName } from '../lib/billingTarget';
import { useActingAthlete } from '../context/ActingAthleteContext';

// player_parent/base_user deliberately absent: which of those two wire
// roles a user has is not the same question as whether they're paying
// TODAY (a base_user can be comped, and PAYMENTS_REQUIRED=false grants pro
// to everyone regardless of role) — same wire-role-means-entitlement
// mistake Task 2 already removed from PlayerStatsPage/MePanel. The athlete
// suffix ("· ACTIVE" / "· FREE TIER") is a BILLING STATUS label ("does this
// account have a subscription row?"), not an entitlement ("what may this
// account do?") — it's derived below from the /users/me/subscription fetch
// this page already makes for the Billing tab, not from useEntitlements().
const ROLE_LABEL = {
  admin:           'ADMIN',
  internal_staff:  'INTERNAL STAFF',
  coach:           'COACH',
  basic_coach:     'COACH · PENDING',
  partner:         'PARTNER',
  guardian:        'GUARDIAN',
  athlete:         'ATHLETE',
  marketing:       'MARKETING',
};

const TABS_ALL = [
  { id: 'account', label: 'My Account' },
  { id: 'billing', label: 'Billing' },
  { id: 'preferences', label: 'Preferences' },
];

const TABS_PLAYER = [
  { id: 'account', label: 'My Account' },
  { id: 'billing', label: 'Billing' },
  { id: 'preferences', label: 'Preferences' },
];

const TABS_COACH = [
  { id: 'account', label: 'My Account' },
  { id: 'preferences', label: 'Preferences' },
];

export default function SettingsPage() {
  const { user, API_URL, logout, refreshUser } = useAuth();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const [activeTab, setActiveTab] = useState('account');
  const [message, setMessage] = useState('');
  const [messageType, setMessageType] = useState('success');

  // Photo state — 3-slot gallery (indexes 0-2 for slots 1-3)
  const [photos, setPhotos] = useState([null, null, null]);
  const [primaryIndex, setPrimaryIndex] = useState(1);
  const [logoPreview, setLogoPreview] = useState(null);

  // Preferences state
  const [unitSystem, setUnitSystem] = useState(
    user?.unit_preference || localStorage.getItem('prozpects_units') || 'us'
  );
  const [saving, setSaving] = useState(false);

  // Billing state
  const [subscription, setSubscription] = useState(null);
  // Tracks a failed fetchSubscription() call separately from `subscription`
  // itself, since the catch below leaves `subscription` set to a fallback
  // {status:'none', plan_type:'free'} object (for the existing Billing tab
  // rendering, unchanged) that would otherwise be indistinguishable from a
  // resolved "no subscription row" response when deriving the roleLabel
  // badge further down.
  const [subscriptionFetchFailed, setSubscriptionFetchFailed] = useState(false);
  const [billingConfig, setBillingConfig] = useState(null);
  const [billingLoading, setBillingLoading] = useState(false);

  // Editable account fields
  const [accountFields, setAccountFields] = useState({
    first_name: '',
    last_name: '',
    email: '',
    date_of_birth: '',
    phone: '',
    scheduling_link: ''
  });
  const [emailError, setEmailError] = useState('');

  // Modal state
  const [showResetModal, setShowResetModal] = useState(false);
  const [resetSent, setResetSent] = useState(false);
  const [showCancelModal, setShowCancelModal] = useState(false);
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [deleteStep, setDeleteStep] = useState(1);
  const [deleteConfirmText, setDeleteConfirmText] = useState('');
  const [actionLoading, setActionLoading] = useState(false);
  const [downloadingData, setDownloadingData] = useState(false);
  const [emailChangeModal, setEmailChangeModal] = useState(null);  // { newEmail }
  const [emailChangePassword, setEmailChangePassword] = useState('');

  const isPlayer = isAthleteRole(user?.role);
  const isCoach = user?.role === 'coach';
  const hasPhotos = isPlayer || isCoach;
  // A guardian is the PAYER for their athlete's plan, so they see and manage
  // it here — the tab shows the ATHLETE's subscription, never a guardian
  // subscription (there is no such thing).
  const isGuardian = user?.role === 'guardian';
  const canSeeBilling = isPlayer || isGuardian;
  // Whose plan the billing actions act on — see lib/billingTarget.js.
  const billingTarget = useBillingTarget();
  const billingTargetId = billingTarget.athleteUserId;
  const { actingAthleteName } = useActingAthlete();
  const billingAthleteName = isGuardian
    ? billingTargetName({
        athleteUserId: billingTargetId,
        managedAthletes: billingTarget.managedAthletes,
        actingAthleteName,
      })
    : null;
  // Guardian billing content waits for a target: with several linked
  // athletes the picker comes first, with none the "link your athlete"
  // message stands alone. No status read, no actions, until then.
  const showBillingDetail = !isGuardian || billingTargetId != null;
  // `subscription` holds the ATHLETE's row when a guardian is viewing, so
  // anything about THIS account (the delete-account flow, the danger zone)
  // must read this instead — a guardian account has no subscription of its
  // own to cancel.
  const ownSubscription = isGuardian ? null : subscription;
  // Admin, staff and partner get the settings index + form inside the rail. Their tab list drops
  // Billing: it only has content for athletes and guardians.
  const railView = usesRail(user?.role);
  const tabs = isPlayer ? TABS_PLAYER : isCoach ? TABS_COACH
    : railView ? TABS_ALL.filter((t) => t.id !== 'billing') : TABS_ALL;

  // Billing service is at /billing/ via nginx proxy
  const BILLING_URL = `${window.location.origin}/billing`;

  const getAxiosConfig = () => ({
    headers: { 'Authorization': `Bearer ${localStorage.getItem('token')}` }
  });

  const showMessage = (text, type = 'success') => {
    setMessage(text);
    setMessageType(type);
    setTimeout(() => setMessage(''), 4000);
  };

  // Handle billing return from Stripe and direct tab links
  useEffect(() => {
    const billingStatus = searchParams.get('billing');
    const tab = searchParams.get('tab');
    if (billingStatus === 'success') {
      setActiveTab('billing');
      showMessage('Subscription activated! Welcome to ProZpects Athlete.');
      // The read + retries live in their own effect below — they have to
      // wait for a guardian's target, which is not known at mount.
    } else if (billingStatus === 'canceled') {
      setActiveTab('billing');
      showMessage('Checkout was canceled. No charges were made.', 'error');
    } else if (tab === 'billing') {
      setActiveTab('billing');
    }
  }, [searchParams]);

  // Webhook lag after a Stripe return: re-read the plan now and again at
  // 2s/5s/10s, because the webhook that writes the row can land after the
  // redirect. Keyed on the resolved GUARDIAN target (null for everyone
  // else, so an athlete's deps never change and this runs exactly once on
  // mount, as it always did) — a guardian's target arrives with the roster,
  // several seconds after mount, and a schedule started before then would
  // close over a null id and no-op every time.
  const returnedFromCheckout = searchParams.get('billing') === 'success';
  const billingRetryTarget = isGuardian ? billingTargetId : null;
  useEffect(() => {
    if (!returnedFromCheckout) return undefined;
    if (isGuardian && billingRetryTarget == null) return undefined;
    fetchSubscription(billingRetryTarget);
    const timers = [2000, 5000, 10000].map(
      (delay) => setTimeout(() => fetchSubscription(billingRetryTarget), delay),
    );
    return () => timers.forEach(clearTimeout);
  }, [returnedFromCheckout, isGuardian, billingRetryTarget]);

  useEffect(() => {
    if (user?.unit_preference) {
      setUnitSystem(user.unit_preference);
      localStorage.setItem('prozpects_units', user.unit_preference);
    }
    if (user) {
      setAccountFields({
        first_name: user.first_name || '',
        last_name: user.last_name || '',
        email: user.email || '',
        date_of_birth: user.date_of_birth || '',
        phone: user.phone || '',
        scheduling_link: user.scheduling_link || ''
      });
    }
  }, [user]);

  useEffect(() => {
    if (hasPhotos) {
      fetchPhotos();
    }
    if (canSeeBilling) {
      // A guardian's plan read is keyed on the resolved athlete, which is
      // not known at mount — the effect below owns that fetch.
      if (!isGuardian) fetchSubscription();
      fetchBillingConfig();
    }
  }, []);

  // A guardian's Billing tab shows the ATHLETE's plan: (re)read it whenever
  // the resolved target changes — the roster landing, or the picker answered.
  useEffect(() => {
    if (!isGuardian) return;
    if (billingTargetId == null) {
      setSubscription(null);
      setSubscriptionFetchFailed(false);
      return;
    }
    fetchSubscription(billingTargetId);
  }, [isGuardian, billingTargetId]);

  const fetchPhotos = async () => {
    try {
      const response = await axios.get(`${API_URL}/athletes/profile`, getAxiosConfig());
      if (response.data) {
        setPhotos([
          response.data.player_photo || null,
          response.data.player_photo_2 || null,
          response.data.player_photo_3 || null,
        ]);
        setPrimaryIndex(response.data.primary_photo_index || 1);
        if (response.data.school_logo) setLogoPreview(response.data.school_logo);
      }
    } catch (error) {
      console.error('Error fetching photos:', error);
    }
  };

  const fetchSubscription = async (targetOverride) => {
    if (!user) return;
    // A guardian reads the athlete's plan from billing-service, which
    // authorizes a linked guardian (authorize_payment_for_athlete). The
    // /users/me/subscription route is the CALLER's row and would report the
    // guardian's own (non-existent) plan.
    const guardianTarget = isGuardian ? (targetOverride ?? billingTargetId) : null;
    if (isGuardian && guardianTarget == null) return;
    try {
      // Try main backend first (always available)
      const response = isGuardian
        ? await axios.get(`${BILLING_URL}/status/${guardianTarget}`, getAxiosConfig())
        : await axios.get(`${API_URL}/users/me/subscription`, getAxiosConfig());
      setSubscription(response.data);
      setSubscriptionFetchFailed(false);
    } catch (error) {
      console.error('Error fetching subscription:', error);
      setSubscription({ status: 'none', plan_type: 'free' });
      setSubscriptionFetchFailed(true);
    }
  };

  const fetchBillingConfig = async () => {
    try {
      const response = await axios.get(`${BILLING_URL}/config`);
      setBillingConfig(response.data);
    } catch (error) {
      console.error('Error fetching billing config:', error);
    }
  };

  const handlePhotoUpload = async (e, photoType, slot = 1) => {
    const file = e.target.files[0];
    if (!file) return;

    const formData = new FormData();
    formData.append('file', file);
    formData.append('photo_type', photoType);
    if (photoType === 'player') formData.append('slot', slot);

    try {
      const response = await axios.post(
        `${API_URL}/athletes/upload-photo`,
        formData,
        {
          ...getAxiosConfig(),
          headers: {
            ...getAxiosConfig().headers,
            'Content-Type': 'multipart/form-data'
          }
        }
      );

      if (photoType === 'player') {
        const updated = [...photos];
        updated[slot - 1] = response.data.filepath;
        setPhotos(updated);
        // Auto-set primary if it's the first photo
        if (!photos.some(p => p)) setPrimaryIndex(slot);
        showMessage('Photo uploaded successfully!');
      } else {
        setLogoPreview(response.data.filepath);
        showMessage('Logo uploaded successfully!');
      }
    } catch (error) {
      showMessage('Error uploading photo: ' + (error.response?.data?.detail || error.message), 'error');
    }
  };

  const handleDeletePhoto = async (slot) => {
    try {
      await axios.delete(`${API_URL}/athletes/delete-photo`, {
        ...getAxiosConfig(),
        data: { slot }
      });
      const updated = [...photos];
      updated[slot - 1] = null;
      setPhotos(updated);
      // If deleted was primary, shift to first available
      if (primaryIndex === slot) {
        const next = updated.findIndex(p => p);
        setPrimaryIndex(next >= 0 ? next + 1 : 1);
      }
      showMessage('Photo deleted.');
    } catch (error) {
      showMessage('Error deleting photo: ' + (error.response?.data?.detail || error.message), 'error');
    }
  };

  const handleSetPrimary = async (slot) => {
    try {
      await axios.post(`${API_URL}/athletes/set-primary-photo`, { slot }, getAxiosConfig());
      setPrimaryIndex(slot);
      showMessage('Primary photo updated!');
    } catch (error) {
      showMessage('Error setting primary: ' + (error.response?.data?.detail || error.message), 'error');
    }
  };

  const handleUnitChange = async (value) => {
    setUnitSystem(value);
    localStorage.setItem('prozpects_units', value);
    setSaving(true);

    try {
      await axios.put(
        `${API_URL}/users/preferences`,
        { unit_preference: value },
        getAxiosConfig()
      );
      showMessage('Display units updated!');
    } catch (error) {
      console.error('Error saving preference:', error);
      showMessage('Saved locally. Server sync will retry on next login.', 'error');
    }
    setSaving(false);
  };

  const handleAccountFieldChange = (e) => {
    const { name, value } = e.target;
    setAccountFields({ ...accountFields, [name]: value });
    if (name === 'email') setEmailError('');
  };

  const isValidEmail = (email) => /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email);

  const handleNameBlur = async () => {
    const first = accountFields.first_name.trim();
    const last = accountFields.last_name.trim();
    if (!first || !last) return;
    if (first === user?.first_name && last === user?.last_name) return;
    try {
      await axios.put(`${API_URL}/users/me/name`, { first_name: first, last_name: last }, getAxiosConfig());
      await refreshUser();
      showMessage('Name updated!');
    } catch (error) {
      showMessage(error.response?.data?.detail || 'Error updating name', 'error');
    }
  };

  const handlePhoneBlur = async () => {
    const phone = accountFields.phone.trim();
    if (phone === (user?.phone || '')) return;
    try {
      // Saves to User.phone — the number concierge SMS "include my cell" texts.
      await axios.put(`${API_URL}/users/me/phone`, { phone }, getAxiosConfig());
      await refreshUser();
      showMessage('Phone number updated!');
    } catch (error) {
      showMessage(error.response?.data?.detail || 'Error updating phone number', 'error');
    }
  };

  const handleSchedulingLinkBlur = async () => {
    const link = accountFields.scheduling_link.trim();
    if (link === (user?.scheduling_link || '')) return;
    try {
      await axios.put(`${API_URL}/users/me/scheduling-link`, { scheduling_link: link }, getAxiosConfig());
      await refreshUser();
      showMessage('Scheduling link updated!');
    } catch (error) {
      showMessage(error.response?.data?.detail || 'Error updating scheduling link', 'error');
    }
  };

  const handleEmailBlur = () => {
    const email = accountFields.email.trim();
    if (!email || email === user?.email) { setEmailError(''); return; }
    if (!isValidEmail(email)) {
      setEmailError('Please enter a valid email address');
      return;
    }
    setEmailError('');
    setEmailChangePassword('');
    setEmailChangeModal({ newEmail: email });
  };

  const handleConfirmEmailChange = async () => {
    if (!emailChangeModal) return;
    if (!emailChangePassword) {
      setEmailError('Enter your current password to continue');
      return;
    }
    try {
      await axios.put(
        `${API_URL}/users/me/email`,
        { new_email: emailChangeModal.newEmail, current_password: emailChangePassword },
        getAxiosConfig(),
      );
      setEmailChangeModal(null);
      setEmailChangePassword('');
      // Restore the field to the current (still-unchanged) email — the swap
      // doesn't take effect until they click the link in the new inbox.
      setAccountFields((f) => ({ ...f, email: user?.email || '' }));
      showMessage(`We sent a confirmation link to ${emailChangeModal.newEmail}. Click it to finish the change.`);
    } catch (error) {
      setEmailError(error.response?.data?.detail || 'Error updating email');
    }
  };

  const cancelEmailChangeModal = () => {
    setEmailChangeModal(null);
    setEmailChangePassword('');
    setAccountFields((f) => ({ ...f, email: user?.email || '' }));
    setEmailError('');
  };

  const handleDobBlur = async () => {
    const dob = accountFields.date_of_birth;
    if (!dob || dob === user?.date_of_birth) return;
    try {
      await axios.put(`${API_URL}/users/me/dob`, { date_of_birth: dob }, getAxiosConfig());
      await refreshUser();
      showMessage('Date of birth updated!');
    } catch (error) {
      showMessage(error.response?.data?.detail || 'Error updating date of birth', 'error');
    }
  };

  // ── Billing Actions ──

  const handleCheckout = async (priceKey) => {
    if (billingTargetId == null) return;
    setBillingLoading(true);
    try {
      const response = await axios.post(
        `${BILLING_URL}/checkout/create-session`,
        billingBody(user, billingTargetId, { price_key: priceKey }),
      );
      window.location.href = response.data.checkout_url;
    } catch (error) {
      showMessage('Error starting checkout: ' + (error.response?.data?.detail || error.message), 'error');
      setBillingLoading(false);
    }
  };

  const handleManageBilling = async () => {
    if (billingTargetId == null) return;
    setBillingLoading(true);
    try {
      const response = await axios.post(
        `${BILLING_URL}/portal/create-session`,
        billingBody(user, billingTargetId),
      );
      window.location.href = response.data.portal_url;
    } catch (error) {
      showMessage('Error opening billing portal: ' + (error.response?.data?.detail || error.message), 'error');
      setBillingLoading(false);
    }
  };

  const handleCancelSubscription = async () => {
    if (billingTargetId == null) return;
    setActionLoading(true);
    try {
      const response = await axios.post(`${BILLING_URL}/cancel`, billingBody(user, billingTargetId));
      const until = response.data.access_until
        ? new Date(response.data.access_until).toLocaleDateString()
        : 'the end of your billing period';
      showMessage(`Subscription will cancel at end of billing period. You have access until ${until}.`);
      setShowCancelModal(false);
      fetchSubscription();
    } catch (error) {
      showMessage('Error canceling: ' + (error.response?.data?.detail || error.message), 'error');
    }
    setActionLoading(false);
  };

  // ── Personal Data Export (CCPA) ──

  const handleDownloadMyData = async () => {
    setDownloadingData(true);
    try {
      const response = await axios.get(`${API_URL}/users/me/export`, {
        ...getAxiosConfig(),
        responseType: 'blob',
      });
      const blob = new Blob([response.data], { type: 'application/json' });
      const url = URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      const stamp = new Date().toISOString().slice(0, 10);
      link.download = `prozpects-export-${stamp}.json`;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      URL.revokeObjectURL(url);
    } catch (err) {
      console.error('Download my data failed:', err);
      showMessage('Could not prepare your download. Please try again.', 'error');
    } finally {
      setDownloadingData(false);
    }
  };

  // ── Delete Account Flow ──

  const startDeleteAccount = () => {
    setDeleteStep(1);
    setDeleteConfirmText('');
    setShowDeleteModal(true);
  };

  const handleDeleteAccount = async () => {
    if (deleteStep === 1) {
      // If active subscription, cancel it first
      if (ownSubscription && (ownSubscription.status === 'active' || ownSubscription.status === 'past_due')) {
        setDeleteStep(2);
        setActionLoading(true);
        try {
          // Deleting MY account cancels MY subscription — the account being
          // deleted is the target here, deliberately not the billing helper's
          // acting/linked athlete.
          await axios.post(`${BILLING_URL}/cancel-immediately`, billingBody(user, user.id));
          setDeleteStep(3);
        } catch (error) {
          showMessage('Error canceling subscription. Please try again.', 'error');
          setShowDeleteModal(false);
        }
        setActionLoading(false);
        return;
      }
      setDeleteStep(3);
      return;
    }

    if (deleteStep === 3 && deleteConfirmText === 'DELETE') {
      setActionLoading(true);
      try {
        await axios.delete(`${API_URL}/users/me`, getAxiosConfig());
        logout();
        navigate('/login');
      } catch (error) {
        showMessage('Error deleting account: ' + (error.response?.data?.detail || error.message), 'error');
        setShowDeleteModal(false);
      }
      setActionLoading(false);
    }
  };

  const handleResetPassword = async () => {
    try {
      await axios.post(`${API_URL}/auth/forgot-password`, { email: user?.email });
      setShowResetModal(false);
      setResetSent(true);
      setTimeout(() => setResetSent(false), 6000);
    } catch (error) {
      showMessage('Error sending reset email: ' + (error.response?.data?.detail || error.message), 'error');
      setShowResetModal(false);
    }
  };

  const handleReactivate = async () => {
    setBillingLoading(true);
    try {
      await axios.post(`${API_URL}/users/me/subscription/reactivate`, {}, getAxiosConfig());
      showMessage('Subscription reactivated! Welcome back to ProZpects Athlete.');
      fetchSubscription();
    } catch (error) {
      showMessage('Error reactivating: ' + (error.response?.data?.detail || error.message), 'error');
    }
    setBillingLoading(false);
  };

  // Escape key handlers for modals
  useEffect(() => {
    const handleEsc = (e) => { if (e.key === 'Escape') setShowCancelModal(false); };
    if (showCancelModal) {
      document.addEventListener('keydown', handleEsc);
      return () => document.removeEventListener('keydown', handleEsc);
    }
  }, [showCancelModal]);

  useEffect(() => {
    const handleEsc = (e) => { if (e.key === 'Escape') setShowDeleteModal(false); };
    if (showDeleteModal) {
      document.addEventListener('keydown', handleEsc);
      return () => document.removeEventListener('keydown', handleEsc);
    }
  }, [showDeleteModal]);

  // Both drive account-tab copy about THIS account — ownSubscription, so a
  // guardian's danger zone never describes their athlete's plan as theirs.
  const isSubscribed = ownSubscription && ownSubscription.status === 'active';
  const isCanceling = ownSubscription && ownSubscription.cancel_at_period_end;

  const formatPlanName = (planType) => {
    if (planType === 'player_monthly') return 'Athlete Monthly';
    if (planType === 'player_annual') return 'Athlete Annual';
    return 'Free';
  };

  const initials = ((user?.first_name || '')[0] || '') + ((user?.last_name || '')[0] || '');
  // Billing status, not entitlement — do not derive from
  // useEntitlements().tier (resolves 'pro' for everyone while
  // PAYMENTS_REQUIRED=false) or subscription.status (the fetch above fakes
  // "active" for everyone under the same flag; see get_my_subscription in
  // backend/app/routers/users.py). current_period_end is the one field that
  // endpoint doesn't fake: set only when a subscription row exists (paid or
  // comped), null otherwise.
  //
  // GENEROUS, same direction as every other consumer in this codebase: a
  // failed fetch or a load still in flight (subscription still null) reads
  // as "has a subscription" — only a resolved response confirming no row
  // (current_period_end: null) badges FREE TIER.
  const hasSubscription = subscriptionFetchFailed || !subscription || Boolean(subscription.current_period_end);
  const roleLabel = isPlayer
    ? `ATHLETE · ${hasSubscription ? 'ACTIVE' : 'FREE TIER'}`
    : ROLE_LABEL[user?.role] || (user?.role ? user.role.toUpperCase() : 'ACCOUNT');
  const heroPhoto = photos[primaryIndex - 1] || photos[0] || null;

  const content = (
    <div className={railView ? 'set-rail' : 'ed-page'}>
      {message && (
        <div className={`settings-toast ${messageType === 'error' ? 'settings-toast--error' : ''}`} role="alert">
          {message}
        </div>
      )}

      {!railView && (
      <>
      <EditorHero
        eyebrow={
          <>
            <MonoLabel onDark>SETTINGS · {tabs.find(t => t.id === activeTab)?.label?.toUpperCase() || 'ACCOUNT'}</MonoLabel>
            <MonoLabel onDark>·</MonoLabel>
            <MonoLabel onDark>{roleLabel}</MonoLabel>
          </>
        }
        prefix={<Monogram initials={initials} photoUrl={heroPhoto} />}
        headline={`${user?.first_name || ''} ${user?.last_name || ''}`.trim() || 'Account'}
        sub={user?.email || ''}
      />

      <div className="ed-row" style={{ flexWrap: 'wrap', gap: 8, marginTop: 8 }}>
        {tabs.map(tab => (
          <Pill
            key={tab.id}
            variant={activeTab === tab.id ? 'active' : 'ghost'}
            onClick={() => setActiveTab(tab.id)}
          >
            {tab.label}
          </Pill>
        ))}
      </div>
      </>
      )}

      {railView && (
        <aside className="adm__card set-index" aria-label="Settings sections">
          <div className="set-id">
            <span className="set-id__avatar" aria-hidden="true">{initials}</span>
            <span className="set-id__name">{`${user?.first_name || ''} ${user?.last_name || ''}`.trim() || 'Account'}</span>
            <span className="set-id__sub">{user?.email || ''}</span>
            <span className="adm-chip adm-chip--green">{roleLabel}</span>
          </div>
          {tabs.map((tab) => (
            <button key={tab.id} type="button" className={`ls-irow ${activeTab === tab.id ? 'ls-irow--active' : ''}`}
              aria-current={activeTab === tab.id ? 'true' : undefined} onClick={() => setActiveTab(tab.id)}>
              <span className="ls-irow__title">{tab.label}</span>
            </button>
          ))}
        </aside>
      )}

      <div className="settings-content">

        {/* === MY ACCOUNT TAB === */}
        {activeTab === 'account' && (
          <div>
            <div className="settings-section">
              <h2 className="settings-section__title">Account Information</h2>
              <p className="settings-section__desc">Your account details.</p>

              <div className="settings-form-grid">
                <div className="settings-field">
                  <label className="settings-field__label" htmlFor="settings_first_name">First Name</label>
                  <input
                    id="settings_first_name"
                    className="form-field__input"
                    type="text"
                    name="first_name"
                    value={accountFields.first_name}
                    onChange={handleAccountFieldChange}
                    onBlur={handleNameBlur}
                  />
                </div>
                <div className="settings-field">
                  <label className="settings-field__label" htmlFor="settings_last_name">Last Name</label>
                  <input
                    id="settings_last_name"
                    className="form-field__input"
                    type="text"
                    name="last_name"
                    value={accountFields.last_name}
                    onChange={handleAccountFieldChange}
                    onBlur={handleNameBlur}
                  />
                </div>
                <div className="settings-field">
                  <label className="settings-field__label" htmlFor="settings_email">Email</label>
                  <input
                    id="settings_email"
                    className={`form-field__input ${emailError ? 'form-field__input--error' : ''}`}
                    type="email"
                    name="email"
                    value={accountFields.email}
                    onChange={handleAccountFieldChange}
                    onBlur={handleEmailBlur}
                    disabled={!!user?.google_id}
                  />
                  {emailError && <div className="form-field__error" role="alert">{emailError}</div>}
                </div>
                <div className="settings-field">
                  <label className="settings-field__label" htmlFor="settings_dob">Date of Birth</label>
                  <input
                    id="settings_dob"
                    className="form-field__input"
                    type="date"
                    name="date_of_birth"
                    value={accountFields.date_of_birth}
                    onChange={handleAccountFieldChange}
                    onBlur={handleDobBlur}
                    max={new Date().toISOString().split('T')[0]}
                  />
                </div>
                {(user?.role === 'admin' || user?.role === 'internal_staff' || user?.role === 'partner') && (
                  <div className="settings-field">
                    <label className="settings-field__label" htmlFor="settings_phone">Phone Number</label>
                    <input
                      id="settings_phone"
                      className="form-field__input"
                      type="tel"
                      name="phone"
                      placeholder="(555) 123-4567"
                      value={accountFields.phone}
                      onChange={handleAccountFieldChange}
                      onBlur={handlePhoneBlur}
                    />
                    <p className="settings-section__desc" style={{ marginTop: 6 }}>
                      Used to include you in concierge group texts (the “Include my cell” option).
                    </p>
                  </div>
                )}
                {(user?.role === 'admin' || user?.role === 'internal_staff' || user?.role === 'partner') && (
                  <div className="settings-field" style={{ gridColumn: '1 / -1' }}>
                    <label className="settings-field__label" htmlFor="settings_scheduling_link">Scheduling Link</label>
                    <input
                      id="settings_scheduling_link"
                      className="form-field__input"
                      type="text"
                      name="scheduling_link"
                      placeholder="https://calendly.com/your-link"
                      value={accountFields.scheduling_link}
                      onChange={handleAccountFieldChange}
                      onBlur={handleSchedulingLinkBlur}
                    />
                  </div>
                )}
                <div className="settings-field">
                  <label className="settings-field__label">Role</label>
                  <div className="settings-field__value-display">
                    <span className="settings-role-badge">
                      {user?.role === 'player_parent'
                        ? (subscription && subscription.status === 'active' ? 'Athlete / Parent' : 'Basic User')
                        : user?.role === 'coach' ? 'Coach' :
                         user?.role === 'admin' ? 'Administrator' :
                         user?.role === 'internal_staff' ? 'Internal Staff' :
                         user?.role === 'partner' ? 'Partner' :
                         user?.role}
                    </span>
                  </div>
                </div>
                <div className="settings-field">
                  <label className="settings-field__label">Account Status</label>
                  <div className="settings-field__value-display">
                    <span className={user?.is_active ? 'settings-status--active' : 'settings-status--inactive'}>
                      {user?.is_active ? '✓ Active' : '✗ Inactive'}
                    </span>
                  </div>
                </div>
                {isPlayer && (
                  <div className="settings-field">
                    <label className="settings-field__label">Subscription</label>
                    <div className="settings-field__value-display">
                      {!subscription || subscription.status === 'none' || subscription.plan_type === 'free'
                        ? <span style={{color: '#94a3b8'}}>Free Plan</span>
                        : subscription.status === 'active' && !subscription.cancel_at_period_end
                        ? <span className="settings-status--active">✓ {formatPlanName(subscription.plan_type)}</span>
                        : subscription.status === 'active' && subscription.cancel_at_period_end
                        ? <span style={{color: '#f59e0b', fontWeight: 500}}>Canceling — expires {new Date(subscription.current_period_end).toLocaleDateString()}</span>
                        : subscription.status === 'canceled'
                        ? <span style={{color: '#94a3b8'}}>Canceled</span>
                        : subscription.status === 'past_due'
                        ? <span style={{color: '#ef4444', fontWeight: 500}}>Past Due</span>
                        : <span style={{color: '#94a3b8'}}>{subscription.status}</span>
                      }
                    </div>
                  </div>
                )}
              </div>

              {!user?.google_id && (
                <div style={{ marginTop: '16px' }}>
                  <Button variant="secondary" onClick={() => setShowResetModal(true)}>
                    Reset Password
                  </Button>
                </div>
              )}
            </div>

            {/* Privacy / Your Data */}
            <div className="settings-section">
              <h2 className="settings-section__title">Your Data</h2>
              <p className="settings-section__desc">Download a copy of the personal information ProZpects has stored about you.</p>
              <div style={{ marginTop: '16px' }}>
                <Button variant="secondary" onClick={handleDownloadMyData} disabled={downloadingData}>
                  {downloadingData ? 'Preparing…' : 'Download My Data'}
                </Button>
              </div>
            </div>

            {/* Danger Zone */}
            <div className="settings-section settings-danger-zone">
              <h2 className="settings-section__title settings-danger-zone__title">Danger Zone</h2>
              <p className="settings-section__desc">Permanent actions that cannot be undone.</p>

              <div className="settings-danger-actions">
                {isPlayer && isSubscribed && !isCanceling && (
                  <div className="settings-danger-item">
                    <div>
                      <div className="settings-danger-item__title">Cancel Subscription</div>
                      <div className="settings-danger-item__desc">
                        You'll lose college matching, school research, coach contact details, and unlimited video at the end of your billing period. Your recruiting tracker — schools you follow, visits and offers — stays, and your profile stays live in coach search.
                      </div>
                    </div>
                    <Button variant="danger" onClick={() => setShowCancelModal(true)}>
                      Cancel Subscription
                    </Button>
                  </div>
                )}

                {isPlayer && isCanceling && (
                  <div className="settings-danger-item">
                    <div>
                      <div className="settings-danger-item__title">Subscription Canceling</div>
                      <div className="settings-danger-item__desc">
                        Access continues until {subscription?.current_period_end
                          ? new Date(subscription.current_period_end).toLocaleDateString()
                          : 'the end of your billing period'}.
                      </div>
                    </div>
                  </div>
                )}

                <div className="settings-danger-item">
                  <div>
                    <div className="settings-danger-item__title">Delete Account</div>
                    <div className="settings-danger-item__desc">
                      Permanently delete your account and all data. This cannot be undone.
                    </div>
                  </div>
                  <Button variant="danger" onClick={startDeleteAccount}>
                    Delete account
                  </Button>
                </div>
              </div>
            </div>
          </div>
        )}

        {/* === BILLING TAB === */}
        {activeTab === 'billing' && canSeeBilling && (
          <div>
            <BillingAthletePicker target={billingTarget} />

            {/* Whose plan is this? Only a guardian ever sees someone else's. */}
            {billingAthleteName && (
              <div className="settings-section">
                <h2 className="settings-section__title">{billingAthleteName}'s subscription</h2>
                <p className="settings-section__desc">
                  You're the payer on this plan — the subscription belongs to {billingAthleteName}'s athlete account.
                </p>
              </div>
            )}

            {showBillingDetail && (
              <>
            {/* Current Subscription Status — always show */}
            {subscription && subscription.status !== 'none' && subscription.plan_type !== 'free' && (
              <div className="settings-section">
                <div className={`billing-status-card ${
                  subscription.status === 'active' && !subscription.cancel_at_period_end
                    ? 'billing-status-card--active'
                    : subscription.status === 'past_due'
                    ? 'billing-status-card--pastdue'
                    : subscription.cancel_at_period_end
                    ? 'billing-status-card--canceling'
                    : 'billing-status-card--canceled'
                }`}>
                  <div className="billing-status-badge">
                    {subscription.status === 'active' && !subscription.cancel_at_period_end && 'Active'}
                    {subscription.status === 'active' && subscription.cancel_at_period_end && 'Canceling'}
                    {subscription.status === 'past_due' && 'Past Due'}
                    {subscription.status === 'canceled' && 'Canceled'}
                  </div>
                  <div className="billing-status-title">ProZpects Athlete</div>
                  <div className="billing-status-plan">{formatPlanName(subscription.plan_type)}</div>

                  <div className="billing-details">
                    <div className="billing-detail-row">
                      <span className="billing-detail-label">Status</span>
                      <span className="billing-detail-value">
                        {subscription.status === 'active' && !subscription.cancel_at_period_end && 'Active'}
                        {subscription.status === 'active' && subscription.cancel_at_period_end && 'Canceling at period end'}
                        {subscription.status === 'past_due' && 'Payment failed — please update billing'}
                        {subscription.status === 'canceled' && 'Canceled'}
                      </span>
                    </div>

                    {subscription.current_period_end && (
                      <div className="billing-detail-row">
                        <span className="billing-detail-label">
                          {subscription.status === 'active' && !subscription.cancel_at_period_end
                            ? 'Renews on'
                            : subscription.cancel_at_period_end
                            ? 'Access until'
                            : subscription.status === 'canceled'
                            ? 'Expired on'
                            : 'Period end'}
                        </span>
                        <span className="billing-detail-value">
                          {new Date(subscription.current_period_end).toLocaleDateString('en-US', {
                            year: 'numeric', month: 'long', day: 'numeric'
                          })}
                          {subscription.status === 'active' && !subscription.cancel_at_period_end && (() => {
                            const days = Math.ceil((new Date(subscription.current_period_end) - new Date()) / (1000 * 60 * 60 * 24));
                            return days > 0 ? ` (${days} days)` : '';
                          })()}
                          {subscription.cancel_at_period_end && (() => {
                            const days = Math.ceil((new Date(subscription.current_period_end) - new Date()) / (1000 * 60 * 60 * 24));
                            return days > 0 ? ` (${days} days remaining)` : ' (expired)';
                          })()}
                        </span>
                      </div>
                    )}

                    {subscription.current_period_start && (
                      <div className="billing-detail-row">
                        <span className="billing-detail-label">Current period started</span>
                        <span className="billing-detail-value">
                          {new Date(subscription.current_period_start).toLocaleDateString('en-US', {
                            year: 'numeric', month: 'long', day: 'numeric'
                          })}
                        </span>
                      </div>
                    )}

                    {subscription.canceled_at && (
                      <div className="billing-detail-row">
                        <span className="billing-detail-label">Canceled on</span>
                        <span className="billing-detail-value">
                          {new Date(subscription.canceled_at).toLocaleDateString('en-US', {
                            year: 'numeric', month: 'long', day: 'numeric'
                          })}
                        </span>
                      </div>
                    )}
                  </div>

                  {subscription.status === 'active' && (
                    <Button variant="primary" onClick={handleManageBilling}
                      disabled={billingLoading} style={{ marginTop: '20px' }}>
                      {billingLoading ? 'Opening...' : '→ Manage Billing'}
                    </Button>
                  )}
                  {subscription.status === 'past_due' && (
                    <Button variant="accent" onClick={handleManageBilling}
                      disabled={billingLoading} style={{ marginTop: '20px' }}>
                      {billingLoading ? 'Opening...' : '→ Update Payment Method'}
                    </Button>
                  )}
                  {subscription.status === 'active' && (
                    <p style={{ fontSize: '12px', color: '#94a3b8', marginTop: '8px' }}>
                      Update payment, change plan, view invoices, or cancel
                    </p>
                  )}
                  {/* An athlete cancels from the account tab's danger zone,
                      which is gated to their own account — a guardian has no
                      such row there, so their cancel lives with the plan. */}
                  {isGuardian && subscription.status === 'active' && !subscription.cancel_at_period_end && (
                    <div style={{ marginTop: '8px' }}>
                      <Button variant="danger" onClick={() => setShowCancelModal(true)}>
                        Cancel Subscription
                      </Button>
                    </div>
                  )}
                </div>
              </div>
            )}

            {/* Upgrade CTA — show for free/none/canceled */}
            {(!subscription || subscription.status === 'none' || subscription.status === 'canceled' ||
              (subscription.plan_type === 'free' && subscription.status !== 'active')) && (
              <div className="settings-section">
                <div className="billing-upgrade-card">
                  <div className="billing-upgrade-title">
                    {subscription?.status === 'canceled' ? 'Reactivate ProZpects Athlete' : 'Upgrade to ProZpects Athlete'}
                  </div>
                  <div className="billing-upgrade-desc">
                    Unlock college matching, coach contact details, and unlimited video.
                  </div>

                  <div className="billing-features">
                    <div className="billing-feature-item">✓ See which colleges fit you, and why — ranked across every signal on your profile</div>
                    <div className="billing-feature-item">✓ Coach email, phone, and social handles</div>
                    <div className="billing-feature-item">✓ Unlimited video</div>
                    <div className="billing-feature-item">✓ Unlimited player cards</div>
                  </div>

                  <div style={{ display: 'flex', flexDirection: 'column', gap: '10px', marginTop: '20px', maxWidth: '320px', margin: '20px auto 0' }}>
                    <Button variant="accent" fullWidth onClick={() => handleCheckout('monthly')}
                      disabled={billingLoading || billingTargetId == null} style={{ padding: '14px 32px', fontSize: '15px' }}>
                      {billingLoading ? 'Loading...' : 'Monthly — $19.99/mo'}
                    </Button>
                    <Button variant="accent" fullWidth onClick={() => handleCheckout('annual')}
                      disabled={billingLoading || billingTargetId == null} style={{ padding: '14px 32px', fontSize: '15px' }}>
                      {billingLoading ? 'Loading...' : 'Annual — $199.99/yr (Save 17%)'}
                    </Button>
                  </div>
                </div>
              </div>
            )}
              </>
            )}
          </div>
        )}

        {/* === PREFERENCES TAB === */}
        {activeTab === 'preferences' && (
          <div>
            <div className="settings-section">
              <h2 className="settings-section__title">Display Units</h2>
              <p className="settings-section__desc">Choose how measurements are displayed throughout the platform.</p>

              <div className="settings-unit-options">
                <label className={`settings-unit-card ${unitSystem === 'us' ? 'settings-unit-card--selected' : ''}`}>
                  <input type="radio" name="units" value="us" checked={unitSystem === 'us'}
                    onChange={() => handleUnitChange('us')} disabled={saving} style={{display: 'none'}} />
                  <div className="settings-unit-card__header">
                    <span className="settings-unit-card__icon">🇺🇸</span>
                    <span className="settings-unit-card__title">US Standard</span>
                    {unitSystem === 'us' && <span className="settings-unit-card__check">✓</span>}
                  </div>
                  <div className="settings-unit-card__examples">
                    Height: 6'2" · Weight: 185 lbs · Distance: miles
                  </div>
                </label>

                <label className={`settings-unit-card ${unitSystem === 'metric' ? 'settings-unit-card--selected' : ''}`}>
                  <input type="radio" name="units" value="metric" checked={unitSystem === 'metric'}
                    onChange={() => handleUnitChange('metric')} disabled={saving} style={{display: 'none'}} />
                  <div className="settings-unit-card__header">
                    <span className="settings-unit-card__icon">🌍</span>
                    <span className="settings-unit-card__title">Metric</span>
                    {unitSystem === 'metric' && <span className="settings-unit-card__check">✓</span>}
                  </div>
                  <div className="settings-unit-card__examples">
                    Height: 188 cm · Weight: 84 kg · Distance: km
                  </div>
                </label>
              </div>
            </div>
          </div>
        )}
      </div>

      {/* === EMAIL CHANGE CONFIRM MODAL === */}
      {emailChangeModal && (
        <div className="modal-overlay" role="dialog" aria-modal="true" aria-labelledby="email-change-title" onClick={cancelEmailChangeModal}>
          <div className="modal-panel" style={{ maxWidth: '460px' }} onClick={(e) => e.stopPropagation()}>
            <button className="modal-close" onClick={cancelEmailChangeModal} aria-label="Close email change dialog">×</button>
            <div className="modal-title" id="email-change-title">Confirm Email Change</div>
            <p className="modal-desc">
              We'll send a confirmation link to <strong>{emailChangeModal.newEmail}</strong>. Your email won't change until you click that link.
            </p>
            <p className="modal-desc">Enter your current password to continue:</p>
            <input
              type="password"
              className="form-field__input"
              value={emailChangePassword}
              onChange={(e) => setEmailChangePassword(e.target.value)}
              placeholder="Current password"
              autoFocus
              style={{ marginTop: '8px' }}
              onKeyDown={(e) => { if (e.key === 'Enter') handleConfirmEmailChange(); }}
            />
            {emailError && <div className="settings-form-error" style={{ marginTop: '8px' }}>{emailError}</div>}
            <div className="modal-actions" style={{ marginTop: '16px' }}>
              <Button variant="secondary" onClick={cancelEmailChangeModal}>Cancel</Button>
              <Button variant="primary" onClick={handleConfirmEmailChange}>Send Confirmation</Button>
            </div>
          </div>
        </div>
      )}

      {/* === RESET PASSWORD SENT TOAST === */}
      {resetSent && (
        <div className="settings-toast" role="alert">
          Check your inbox for a link to reset your password.
        </div>
      )}

      {/* === RESET PASSWORD MODAL === */}
      {showResetModal && (
        <div className="modal-overlay" role="dialog" aria-modal="true" aria-labelledby="reset-modal-title" onClick={() => setShowResetModal(false)}>
          <div className="modal-panel" style={{ maxWidth: '440px' }} onClick={(e) => e.stopPropagation()}>
            <button className="modal-close" onClick={() => setShowResetModal(false)} aria-label="Close reset password dialog">×</button>
            <div className="modal-title" id="reset-modal-title">Reset Password</div>
            <p style={{ color: '#475569', marginBottom: '24px', lineHeight: '1.6' }}>
              A password reset link will be sent to <strong>{user?.email}</strong>.
            </p>
            <div className="modal-actions">
              <Button variant="secondary" onClick={() => setShowResetModal(false)}>
                Cancel
              </Button>
              <Button variant="primary" onClick={handleResetPassword}>
                Send Reset Email
              </Button>
            </div>
          </div>
        </div>
      )}

      {/* === CANCEL SUBSCRIPTION MODAL === */}
      {showCancelModal && (
        <div className="modal-overlay" role="dialog" aria-modal="true" aria-labelledby="cancel-modal-title" onClick={() => setShowCancelModal(false)}>
          <div className="modal-panel" style={{ maxWidth: '480px' }} onClick={(e) => e.stopPropagation()}>
            <button className="modal-close" onClick={() => setShowCancelModal(false)} aria-label="Close cancel subscription dialog">×</button>
            <div className="modal-title" id="cancel-modal-title" style={{ color: '#ef4444' }}>Cancel Subscription</div>

            <p style={{ color: '#475569', marginBottom: '16px', lineHeight: '1.6' }}>
              Are you sure you want to cancel {billingAthleteName ? `${billingAthleteName}'s` : 'your'} ProZpects Athlete subscription?
            </p>

            <div className="cancel-consequences">
              <div className="cancel-consequence-item">✗ Your profile will be hidden from coach search</div>
              <div className="cancel-consequence-item">✗ You won't be able to upload photos or stats</div>
              <div className="cancel-consequence-item">✗ Coaches won't be able to message you</div>
            </div>

            <p style={{ color: '#64748b', fontSize: '13px', marginTop: '16px', marginBottom: '24px' }}>
              You'll keep access until the end of your current billing period
              {subscription?.current_period_end && (
                <> ({new Date(subscription.current_period_end).toLocaleDateString()})</>
              )}.
            </p>

            <div className="modal-actions">
              <Button variant="secondary" onClick={() => setShowCancelModal(false)}>
                Keep Subscription
              </Button>
              <Button variant="danger" onClick={handleCancelSubscription} disabled={actionLoading}>
                {actionLoading ? 'Canceling...' : 'Yes, Cancel'}
              </Button>
            </div>
          </div>
        </div>
      )}

      {/* === DELETE ACCOUNT MODAL === */}
      {showDeleteModal && (
        <div className="modal-overlay" role="dialog" aria-modal="true" aria-labelledby="delete-modal-title" onClick={() => setShowDeleteModal(false)}>
          <div className="modal-panel" style={{ maxWidth: '480px' }} onClick={(e) => e.stopPropagation()}>
            <button className="modal-close" onClick={() => setShowDeleteModal(false)} aria-label="Close delete account dialog">×</button>

            {deleteStep === 1 && (
              <>
                <div className="modal-title" id="delete-modal-title" style={{ color: '#ef4444' }}>Delete Account</div>
                <p style={{ color: '#475569', marginBottom: '16px', lineHeight: '1.6' }}>
                  This will permanently delete your account and all associated data:
                </p>
                <div className="cancel-consequences">
                  <div className="cancel-consequence-item">✗ Your profile and all stats</div>
                  <div className="cancel-consequence-item">✗ Photos and school logo</div>
                  <div className="cancel-consequence-item">✗ All messages sent and received</div>
                  {isSubscribed && (
                    <div className="cancel-consequence-item">✗ Your active subscription (will be canceled immediately)</div>
                  )}
                </div>
                <p style={{ color: '#ef4444', fontSize: '14px', fontWeight: '600', marginTop: '16px', marginBottom: '24px' }}>
                  This action cannot be undone.
                </p>
                <div className="modal-actions">
                  <Button variant="secondary" onClick={() => setShowDeleteModal(false)}>
                    Cancel
                  </Button>
                  <Button variant="danger" onClick={handleDeleteAccount}>
                    {isSubscribed ? 'Continue — Cancel Sub First' : 'Continue'}
                  </Button>
                </div>
              </>
            )}

            {deleteStep === 2 && (
              <>
                <div className="modal-title">Canceling Subscription...</div>
                <div style={{ textAlign: 'center', padding: '32px 0' }}>
                  <div style={{ fontSize: '36px', marginBottom: '16px' }}>⏳</div>
                  <p style={{ color: '#475569' }}>
                    Canceling your Stripe subscription before deleting your account...
                  </p>
                </div>
              </>
            )}

            {deleteStep === 3 && (
              <>
                <div className="modal-title" style={{ color: '#ef4444' }}>Final Confirmation</div>
                {ownSubscription?.status === 'active' && (
                  <p style={{ color: '#10b981', fontSize: '14px', marginBottom: '12px', fontWeight: '500' }}>
                    ✓ Subscription has been canceled successfully.
                  </p>
                )}
                <p style={{ color: '#475569', marginBottom: '20px', lineHeight: '1.6' }}>
                  To confirm permanent account deletion, type <strong>DELETE</strong> below:
                </p>
                <input
                  type="text"
                  value={deleteConfirmText}
                  onChange={(e) => setDeleteConfirmText(e.target.value.toUpperCase())}
                  placeholder="Type DELETE to confirm"
                  aria-label="Type DELETE to confirm account deletion"
                  style={{
                    width: '100%',
                    padding: '12px 16px',
                    fontSize: '16px',
                    border: '2px solid #e2e8f0',
                    borderRadius: '8px',
                    marginBottom: '24px',
                    letterSpacing: '3px',
                    textAlign: 'center',
                    fontWeight: '600',
                    boxSizing: 'border-box',
                  }}
                  autoFocus
                />
                <div className="modal-actions">
                  <Button variant="secondary" onClick={() => setShowDeleteModal(false)}>
                    Cancel
                  </Button>
                  <Button
                    variant="danger"
                    onClick={handleDeleteAccount}
                    disabled={deleteConfirmText !== 'DELETE' || actionLoading}
                  >
                    {actionLoading ? 'Deleting...' : 'Permanently Delete Account'}
                  </Button>
                </div>
              </>
            )}
          </div>
        </div>
      )}
    </div>
  );
  return railView
    ? <AdminPage active={null} title="Settings" subtitle="Your account and display preferences.">{content}</AdminPage>
    : content;
}
